/**
 * Executar requisição com retry automático
 * 
 * @param {Function} requestFn - Função que retorna uma Promise (ex: () => api.post(...))
 * @param {Object} options - Opções de retry
 * @param {number} options.maxRetries - Número máximo de tentativas (padrão: 3)
 * @param {number} options.baseDelay - Delay base em ms (padrão: 1000)
 * @param {Function} options.onRetry - Callback chamado em cada retry (opcional)
 * @param {Function} options.shouldRetry - Função para determinar se deve fazer retry (opcional)
 * @param {Function} options.onProgress - Callback para atualizar UI (opcional)
 * @param {AbortSignal} options.signal - Signal para cancelar a requisição (opcional)
 * @returns {Promise} Resultado da requisição
 */
export async function retryRequest(requestFn, options = {}) {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    onRetry = null,
    shouldRetry = null,
    onProgress = null
  } = options;

  let lastError = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // REMOVER verificação de signal.aborted antes de cada tentativa
    // Quando axios dá timeout, ele pode abortar o signal automaticamente
    // Isso impede retries legítimos quando há timeout
    // A verificação será feita apenas se o erro for explicitamente um AbortError
    
    try {
      // Notificar início da tentativa
      if (onProgress && attempt > 0) {
        onProgress({
          attempt: attempt + 1,
          maxRetries,
          status: 'retrying',
          delay: baseDelay * Math.pow(2, attempt - 1)
        });
      }
      
      const result = await requestFn();
      
      // Notificar sucesso
      if (onProgress) {
        onProgress({
          attempt: attempt + 1,
          maxRetries,
          status: 'success'
        });
      }
      
      return result;
    } catch (error) {
      lastError = error;
      
      // Verificar se o erro é explicitamente um AbortError
      // Não verificar signal.aborted aqui, pois pode ser abortado por timeout do axios
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        throw error;
      }
      
      // Verificar se deve fazer retry
      if (shouldRetry && !shouldRetry(error, attempt)) {
        throw error;
      }
      
      // Verificar se é erro que justifica retry
      const isRetryable = isRetryableError(error);
      if (!isRetryable) {
        throw error;
      }
      
      // Se é a última tentativa, lançar erro
      if (attempt >= maxRetries - 1) {
        if (onProgress) {
          onProgress({
            attempt: maxRetries,
            maxRetries,
            status: 'failed',
            error: error.message
          });
        }
        throw error;
      }
      
      // Calcular delay com backoff exponencial
      const delay = baseDelay * Math.pow(2, attempt);
      
      // Callback de retry
      if (onRetry) {
        onRetry(attempt + 1, maxRetries, delay, error);
      }
      
      // REMOVER verificação de signal.aborted antes do sleep também
      // Isso pode estar impedindo retries legítimos
      
      // Aguardar antes de tentar novamente
      await sleep(delay);
    }
  }
  
  throw lastError;
}

/**
 * Verificar se o erro é retryable (timeout, conexão, etc)
 */
function isRetryableError(error) {
  // Timeout
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return true;
  }
  
  // Erro de conexão
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
    return true;
  }
  
  // Status 500, 502, 503, 504 (erros do servidor)
  const status = error.response?.status;
  if (status && [500, 502, 503, 504].includes(status)) {
    return true;
  }
  
  // Status 408 (Request Timeout)
  if (status === 408) {
    return true;
  }
  
  return false;
}

/**
 * Sleep helper
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
